// src/components/MainHeader.js
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import MenuDrawer from "./MenuDrawer";
import SearchOverlay from "./SearchOverlay";
import { useCart } from "../context/CartContext";
import { apiRequest } from "../services/api";

function MainHeader() {
  const { lang, toggleLanguage } = useContext(LanguageContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { cartItems, openDrawer } = useCart();
  const navigate = useNavigate();

  const cartCount = cartItems.reduce((sum, item) => sum + item.qty, 0);

const [isLoggedIn, setIsLoggedIn] = useState(
  !!localStorage.getItem("esill-token")
);

useEffect(() => {
  const syncAuth = () =>
    setIsLoggedIn(!!localStorage.getItem("esill-token"));

  window.addEventListener("auth-changed", syncAuth);
  return () => window.removeEventListener("auth-changed", syncAuth);
}, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

const handleLoginClick = () => {
  localStorage.setItem("esill-open-login", "1");
  window.dispatchEvent(new Event("open-entry-modal"));
};

const handleLogoutClick = () => {
  localStorage.removeItem("esill-token");
  localStorage.removeItem("esill-user");

  window.dispatchEvent(new Event("auth-changed"));
  navigate("/"); // optional but clean
};

  const ItalianFlag = (
    <svg
      width="14"
      height="10"
      viewBox="0 0 3 2"
      className="inline-block ml-1"
      style={{ borderRadius: "2px", boxShadow: "0 0 2px rgba(0,0,0,0.25)" }}
    >
      <rect width="1" height="2" x="0" fill="#009246" />
      <rect width="1" height="2" x="1" fill="#F1F2F1" />
      <rect width="1" height="2" x="2" fill="#CE2B37" />
    </svg>
  );

  return (
    <>
      <div
        dir="ltr" // 🔥 keep header always LTR
        className={`
          fixed left-0 w-full 
          flex items-center justify-between 
          px-4 transition-all duration-300 
          z-[9998] bg-white/80 backdrop-blur-md
          ${isScrolled ? "py-2 shadow-md" : "py-4 shadow-sm"}
        `}
        style={{
          top: isScrolled ? "0px" : "34px",
        }}
      >
        {/* LEFT: Menu + Search */}
        <div className="flex items-center gap-3">
          <button onClick={() => {
    setIsDrawerOpen(true);
    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "open_menu_header",
        entity_type: "navigation",
        entity_id: null,
      }),
    }).catch(() => {});
 }}
>
            <div className="space-y-[4px] transition-all duration-200">
              <span className="block w-[22px] h-[2px] bg-black"></span>
              <span className="block w-[22px] h-[2px] bg-black"></span>
              <span className="block w-[22px] h-[2px] bg-black"></span>
            </div>
          </button>

          <button onClick={() => setIsSearchOpen(true)}>
            <img
              src="/icons/search.svg"
              alt="Search"
              className="w-6 h-6 md:w-7 md:h-7 transition-transform duration-300 hover:scale-110"
            />
          </button>
        </div>

        {/* CENTER: Brand */}
        <div
         className={`
   absolute left-1/2 -translate-x-1/2
   flex flex-col items-center
   transition-all duration-300
   ${isScrolled ? "md:scale-90" : "md:scale-100"}
 `}
          style={{ cursor: "pointer" }}
          onClick={() => {
  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "enter_home_from_header",
      entity_type: "navigation",
      entity_id: null,
    }),
  }).catch(() => {});

  navigate("/");
  window.scrollTo({ top: 0, behavior: "smooth" });
}}

        >
          <span className="font-bold text-lg md:text-xl tracking-wide">
            Esiell
          </span>

          <span className="text-[10px] md:text-xs text-black/70 flex items-center">
            Made in Italy {ItalianFlag}
          </span>
        </div>

        {/* RIGHT: Language + Cart */}
        <div className="flex items-center gap-3">
         {/* LANGUAGE BUTTON */}
<button
  onClick={toggleLanguage}
  title={lang === "EN" ? "Switch to Arabic" : "Switch to English"}
  className="
    w-9 h-9
    flex items-center justify-center
    text-[11px] font-semibold
    border border-silver text-black
    rounded-md
    hover:bg-roseNude
    transition-all duration-200
  "
  style={{ direction: "ltr" }}
>
  {lang === "EN" ? "EN" : "AR"}
</button>

{/* LOGIN / LOGOUT ICON */}
<button
  onClick={isLoggedIn ? handleLogoutClick : handleLoginClick}
  title={isLoggedIn ? (lang === "EN" ? "Logout" : "تسجيل الخروج") : (lang === "EN" ? "Login" : "تسجيل الدخول")}
  className="
    w-9 h-9
    flex items-center justify-center
    border border-silver text-black
    rounded-md
    hover:bg-roseNude
    transition-all duration-200
  "
>
  {isLoggedIn ? (
    /* LOGOUT ICON */
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-[15px] h-[15px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1"
      />
    </svg>
  ) : (
    /* LOGIN / USER ICON */
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-[15px] h-[15px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  )}
</button>


          {/* CART ICON */}
          <button
            id="main-cart-icon"        // 👈 used by flyToCart helper
            onClick={() => {
   apiRequest("/analytics/track", {
     method: "POST",
     body: JSON.stringify({
       action_type: "open_cart_from_header",
       entity_type: "cart",
       entity_id: null,
     }),
   }).catch(() => {});
   openDrawer();
 }}
            className="relative"
          >
            <img
              src="/icons/bag.png"
              alt="Bag"
              className="w-6 h-6 md:w-7 md:h-7 transition-transform duration-300"
            />
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </button>
        </div>
      </div>

      {/* MENU DRAWER */}
      <MenuDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />

      {/* SEARCH OVERLAY */}
<SearchOverlay
  isOpen={isSearchOpen}
  onClose={() => {
    setIsSearchOpen(false);
    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "close_search",
        entity_type: "navigation",
        entity_id: null,
      }),
    }).catch(() => {});
  }}
/>

    </>
  );
}

export default MainHeader;
